import type { KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Departure } from '../lib/departures';
import { useSingleRoomLocations } from '../lib/room-counts';
import { TEAM_STATUS_LABEL, type CalendarTeamSlot } from '../lib/teamQuotes';

export interface SlotCreatePrefill {
  date: string;
  time: string;
  locationId: string;
  // Omitted when the slot has >1 experience — operator picks in NewBookingModal.
  experienceId?: string;
}

interface Props {
  // All Departures sharing the same (date, time_of_day) for this column.
  departures: Departure[];
  // Inspect the slot. Receives every departure in the slot — DepartureModal
  // shows all bookings regardless of experience.
  onSelect: (ds: Departure[]) => void;
  onCreate: (prefill: SlotCreatePrefill) => void;
  // "Alleen geboekt" is on: hide the 0/x chips inside the slot. The screen-level
  // filter only drops whole slots (`booked` is room-wide, so an empty experience
  // sharing a booked room survives it) — this trims the chip list too.
  onlyBooked?: boolean;
  // Team outings holding this slot (work item team-uitjes). Their slots carry a
  // blocked override, so capacity is 0 and the regular cell would not draw.
  teams?: CalendarTeamSlot[];
}

// A slot held by a team outing: company, which part of the group, people and
// status. Click opens the slot's bookings (check-in) when there are any, else
// the request in Teamuitjes.
export function TeamSlotCell({
  teams,
  departures,
  onSelect,
}: {
  teams: CalendarTeamSlot[];
  departures: Departure[];
  onSelect: (ds: Departure[]) => void;
}) {
  const navigate = useNavigate();
  const open = () => {
    if (departures.some((d) => d.booked > 0)) onSelect(departures);
    else navigate(`/teamuitjes?q=${encodeURIComponent(teams[0]!.quote_id)}`);
  };
  return (
    <div
      role="button"
      tabIndex={0}
      className="slot-cell slot-cell-team"
      onClick={open}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          open();
        }
      }}
    >
      {teams.map((t) => (
        <div key={t.quote_id + t.part} className="slot-team">
          <span className={`slot-badge ${t.status === 'requested' ? 'slot-badge-warn' : 'slot-badge-team'}`}>
            {t.status === 'requested' ? 'OFFERTE' : 'TEAMUITJE'}
          </span>
          <div className="slot-team-name">{t.company}</div>
          <div className="slot-team-meta">
            {t.parts > 1 ? `groep ${t.part}/${t.parts} · ` : ''}{t.people} pers.
            {t.parts > 1 ? ` (${t.group_size} totaal)` : ''}
          </div>
          <div className="slot-team-meta">
            {TEAM_STATUS_LABEL[t.status]} · {t.quote_number}
          </div>
        </div>
      ))}
    </div>
  );
}

// Capacity-based hue: green (130) at 0%, red (0) at 100%, yellow (~65) at 50%.
// Light tint for background so chip + total stay legible.
function capacityPalette(ratio: number) {
  const r = Math.max(0, Math.min(1, ratio));
  const hue = 130 - 130 * r;
  return {
    bg: `hsl(${hue} 65% 92%)`,
    fg: `hsl(${hue} 55% 22%)`,
    bar: `hsl(${hue} 55% 42%)`,
  };
}

export function SlotCell({ departures, onSelect, onCreate, onlyBooked = false, teams }: Props) {
  const singleRoomLocations = useSingleRoomLocations();

  if (teams && teams.length > 0) {
    return <TeamSlotCell teams={teams} departures={departures} onSelect={onSelect} />;
  }

  const active = departures.filter((d) => d.capacity > 0);
  if (active.length === 0) return null;

  const first = active[0]!;
  // `capacity` and `booked` are room-wide; every row sharing a room_id
  // carries the same values. Dedupe by room_id, then sum across rooms.
  const byRoom = new Map<string, { capacity: number; booked: number }>();
  for (const d of active) {
    byRoom.set(d.room_id, { capacity: d.capacity, booked: d.booked });
  }
  const totalCapacity = [...byRoom.values()].reduce((n, r) => n + r.capacity, 0);
  const totalBooked   = [...byRoom.values()].reduce((n, r) => n + r.booked,   0);
  // bookings_count + lifecycle_summary remain per-experience — these drive
  // the "all customers arrived" badge.
  const totalBookings = active.reduce((n, d) => n + d.bookings_count, 0);
  const totalArrived  = active.reduce(
    (n, d) => n + (d.lifecycle_summary['arrived'] ?? 0),
    0,
  );

  const ratio = totalCapacity > 0 ? totalBooked / totalCapacity : 0;
  const remaining = Math.max(0, totalCapacity - totalBooked);
  const soldOut = totalCapacity > 0 && remaining === 0;
  const oneLeft = !soldOut && remaining === 1 && totalCapacity > 1;
  const allArrived = totalBookings > 0 && totalArrived === totalBookings;
  // Bookings on a world or room closed by an override (capacity 0) still
  // count: the slot is not empty and their chip stays visible, marked dicht.
  const closedWithGuests = departures.filter((d) => d.capacity === 0 && d.booked_in_experience > 0);
  const empty = totalBooked === 0 && closedWithGuests.length === 0;
  const showRoom = !singleRoomLocations.has(first.location_id);

  const palette = capacityPalette(ratio);

  // Chips are display-only — totals, badges and click routing all stay on the
  // full `active` list. Fall back to every chip when the filter would empty the
  // slot (room-wide `booked` counting a booking no visible row carries).
  const withGuests = active.filter((d) => d.booked_in_experience > 0);
  const chips = [...(onlyBooked && withGuests.length > 0 ? withGuests : active), ...closedWithGuests];

  const openCreate = () => {
    const base: SlotCreatePrefill = {
      date: first.date,
      time: first.time_of_day,
      locationId: first.location_id,
    };
    onCreate(active.length === 1 ? { ...base, experienceId: first.experience_id } : base);
  };

  // Body-click behaviour:
  //  • empty slot → create-flow shortcut (matches legacy DepartureCell UX)
  //  • otherwise  → inspect the whole slot (DepartureModal lists all bookings
  //                  in the slot regardless of experience)
  const onBodyClick = () => {
    if (empty) openCreate();
    else onSelect(departures);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onBodyClick();
    }
  };

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        className={`slot-cell ${empty ? 'slot-cell-empty' : ''} ${soldOut ? 'slot-cell-sold' : ''}`}
        style={{ backgroundColor: palette.bg, color: palette.fg }}
        onClick={onBodyClick}
        onKeyDown={onKeyDown}
        aria-label={`${first.time_of_day} ${totalBooked} van ${totalCapacity} geboekt`}
      >
        {/* Priority: full > everyone-arrived > one-seat-left. Only one badge
            at a time so they don't overlap on narrow mobile cells. */}
        {soldOut ? (
          <span className="slot-badge slot-badge-sold">VOL</span>
        ) : allArrived ? (
          <span className="slot-badge slot-badge-ok">✓ AANGEKOMEN</span>
        ) : oneLeft ? (
          <span className="slot-badge slot-badge-warn">1 PLEK</span>
        ) : null}

        <button
          type="button"
          className="slot-create-btn"
          onClick={(e) => { e.stopPropagation(); openCreate(); }}
          aria-label={`Nieuwe boeking om ${first.time_of_day}`}
          title="Nieuwe boeking"
        >
          +
        </button>

        <div className="slot-chips">
          {chips.map((d) => (
            <span
              key={d.room_id + d.experience_id}
              className={`slot-chip ${d.capacity === 0 ? 'slot-chip-closed' : ''}`}
              title={d.capacity === 0 ? `${d.experience_name}: dicht gezet, ${d.booked_in_experience} geboekt` : undefined}
            >
              <span className="slot-chip-name">{d.experience_name}</span>
              <span className="slot-chip-count">
                {d.capacity === 0 ? `${d.booked_in_experience} · dicht` : `${d.booked_in_experience}/${d.capacity}`}
              </span>
              {showRoom ? <span className="slot-chip-room">{d.room_name}</span> : null}
            </span>
          ))}
        </div>

        <div className="slot-total">
          <span className="slot-total-num">{totalBooked}</span>
          <span className="slot-total-sep"> / </span>
          <span className="slot-total-cap">{totalCapacity}</span>
        </div>

        <div className="slot-bar" aria-hidden="true">
          <div
            className="slot-bar-fill"
            style={{ width: `${Math.min(100, ratio * 100)}%`, backgroundColor: palette.bar }}
          />
        </div>
      </div>
    </>
  );
}
